import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CONFIG } from '@/config-globals';
import { usePathname } from '@/hooks/use-pathname';
import { usePageHeader } from '@/providers/page-header/use-page-header';
import type { BreadcrumbsProps } from './breadcrumbs';
import { ContentLoader } from './loaders';

type ContentProps = {
  isLoading?: boolean;
  breadcrumbs?: BreadcrumbsProps;
  children?: React.ReactNode;
};

export const Content: React.FC<ContentProps> = ({
  isLoading = false,
  breadcrumbs,
  children,
}) => {
  const pathname = usePathname();
  const { setHeader } = usePageHeader();

  useEffect(() => {
    setHeader(breadcrumbs ?? null);
    return () => setHeader(null);
  }, [breadcrumbs, setHeader]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{
          duration: 0.2,
          ease: 'easeOut',
        }}
      >
        <ContentLoader show={isLoading} />
        {typeof breadcrumbs?.title === 'string' && (
          <Helmet key={pathname}>
            <title>
              {CONFIG.APP_NAME} - {breadcrumbs.title}
            </title>
          </Helmet>
        )}
        {children}
      </motion.div>
    </AnimatePresence>
  );
};
